import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, from, map, of, switchMap } from 'rxjs'
import { environment } from 'src/environments/environment'
import { VehicleModel } from '../vehicle/vehicle'

export type LinkStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

/** Situação que as telas mostram: são três, e o que o pedido pediu fica na
 *  etiqueta ao lado, não na situação. */
export type SeguroSituacao = 'PENDING' | 'APPROVED' | 'REJECTED'

export type SeguroTipo = 'VEICULO' | 'VIDA'

export interface SeguroCobertura {
  id: number
  produto_id: number
  nome: string | null
  valor: number | null
}

export interface SeguroAlteracoes {
  contrato_anterior_id: number
  cancelamento: boolean
  encerramento: boolean
  /** No cancelamento o cliente sai junto, salvo em veículo integrado na OON */
  cliente_sai: boolean
  cliente_fica_por_oon: boolean
  cliente_atual: { id: number, name: string } | null
  coberturas_adicionadas: SeguroCobertura[]
  coberturas_removidas: SeguroCobertura[]
  coberturas_mantidas: SeguroCobertura[]
  cliente_anterior: { id: number, name: string } | null
  valor_anterior: number | null
}

export interface ProductLink {
  tipo: SeguroTipo
  substitui_id: number | null
  produtos: SeguroCobertura[]
  valor_total: number
  id: number
  account_id: number
  vehicle_id: number
  customer_id: number | null
  status: LinkStatus
  /** Situação que as telas mostram */
  situation: SeguroSituacao
  /** De onde veio o contrato: criação, alteração ou pedido de cancelamento */
  request_type: 'CREATION' | 'CHANGE' | 'CANCELLATION'
  reason: string | null
  created_by: number | null
  decided_by: number | null
  decided_at: string | null
  ended_reason: 'UNLINKED' | 'REJECTED' | 'SUBSTITUIDO' | null
  ended_by: number | null
  ended_at: string | null
  /** O que o endosso muda no contrato anterior; nulo quando não é endosso */
  alteracoes: SeguroAlteracoes | null
  active: boolean
  created_at: string
  updated_at: string
  created_by_name: string | null
  decided_by_name: string | null
  ended_by_name: string | null
  /** Encerrado como SUBSTITUIDO: tipo do pedido aprovado que o encerrou (alteração ou cancelamento) */
  replaced_by_request_type?: 'CHANGE' | 'CANCELLATION' | null
  vehicle: { id: number, plate: string, brand: string, model: string, type?: string | null, oon_integrated?: boolean } | null
  account: { id: number, name: string } | null
  customer: { id: number, name: string, cpf_cnpj: string, email?: string | null, phone?: string | null } | null
}

export interface ProductLinksResponse {
  results: ProductLink[]
  total_items: number
  total_pages: number
  current_page: number
  page_size: number
  has_next: boolean
  has_prev: boolean
}

export interface ProductLinksFilters {
  page?: number
  page_size?: number
  tipo?: SeguroTipo | null
  status?: string
  request_type?: string
  account_id?: number | null
  product_id?: number | null
  plate?: string
  customer?: string
  start_date?: string
  end_date?: string
}

export interface BreakdownItem {
  id: number
  name: string
  total: number
  pending: number
  approved: number
  rejected: number
  creation: number
  change: number
  cancellation: number
}

export interface MonthlyEvolutionItem {
  month: string
  total: number
  pending: number
  approved: number
  rejected: number
  creation: number
  change: number
  cancellation: number
}

export interface ProductLinksIndicators {
  portfolio: {
    total_vehicles_with_product: number
    active_pending: number
    active_approved: number
    total_products_value: number
    pending_value: number
    approved_value: number
  }
  history: {
    total_links: number
    pending: number
    approved: number
    rejected: number
    creation: number
    change: number
    cancellation: number
    approval_rate: number | null
    avg_approval_hours: number | null
    oldest_pending_hours: number | null
  }
  by_product: BreakdownItem[]
  by_account: BreakdownItem[]
  monthly_evolution: MonthlyEvolutionItem[]
}

/** Situação do veículo: pedido em aberto, contrato aprovado valendo, último
 *  pedido reprovado ou nenhum contrato. */
export type VehicleSeguroStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'NONE'

export interface VehicleCoverage {
  produto_id: number
  nome: string | null
  valor: number | null
}

export interface SeguroVehicle {
  id: number
  plate: string
  brand: string
  model: string
  type: string | null
  year_manufacture: number | null
  year_model: number | string | null
  color: string | null
  status: VehicleSeguroStatus
  oon_integrated: boolean
  has_fipe: boolean
  fipe_value: number | null
  account: { id: number, name: string } | null
  customer: { id: number, name: string, cpf_cnpj: string } | null
  /** Coberturas do contrato que está no veículo (podem ser várias) */
  products: VehicleCoverage[]
  products_total: number
  pending_request_type: 'CREATION' | 'CHANGE' | 'CANCELLATION' | null
  created_at: string | null
}

export interface SeguroVehicleDetail extends Omit<SeguroVehicle, 'account' | 'customer'> {
  vehicle: VehicleModel
  account: {
    id: number
    name: string
    cpf_cnpj: string | null
    tipo_conta: string | null
    oon_integration_enabled: boolean
  } | null
  customer: {
    id: number
    name: string
    cpf_cnpj: string
    email: string | null
    phone: string | null
    address: string | null
    address_number: string | null
    district: string | null
    city: string | null
    state: string | null
    zip_code: string | null
    birth_date: string | null
  } | null
  active_contract: ProductLink | null
  pending_contract: ProductLink | null
  history: ProductLink[]
}

export interface SeguroVehiclesSummary {
  total: number
  pending: number
  approved: number
  rejected: number
  no_contract: number
}

export interface VehicleFilterOption {
  id: number
  name: string
  /** Produto: conta dona, para separar produtos de mesmo nome */
  account_name?: string | null
}

export interface SeguroVehiclesFilterOptions {
  accounts: VehicleFilterOption[]
  products: VehicleFilterOption[]
}

export interface SeguroVehiclesResponse {
  results: SeguroVehicle[]
  summary: SeguroVehiclesSummary
  total_items: number
  total_pages: number
  current_page: number
  page_size: number
  has_next: boolean
  has_prev: boolean
}

/** Como o contrato terminou, para a etiqueta e o detalhe das telas.
 *
 * Até 13/10/2026 o cancelamento aprovado encerrava o contrato anterior como SUBSTITUIDO,
 * igual a uma alteração, e esses registros ficaram assim: quem diz a diferença é o
 * pedido que o encerrou. Depois disso o contrato cancelado já fica UNLINKED. O próprio pedido de
 * cancelamento não ganha etiqueta, porque o tipo do pedido já diz que é cancelamento.
 */
export function contractEndingLabel(link: ProductLink): string {
  if (link.active || !link.ended_reason || link.ended_reason === 'REJECTED') return ''
  if (link.ended_reason === 'SUBSTITUIDO') {
    return link.replaced_by_request_type === 'CANCELLATION' ? 'Contrato cancelado' : 'Alterado por um endosso'
  }
  return link.request_type === 'CANCELLATION' ? '' : 'Contrato cancelado'
}

@Injectable({ providedIn: 'root' })
export class ProductLinksService {
  private base = `${environment.apiUrl}/seguros`

  // Contador de pendências compartilhado entre o painel e o sidebar admin
  public pendingCount$ = new BehaviorSubject<number | null>(null)

  constructor(private http: HttpClient) {}

  refreshPendingCount(): void {
    this.getPendingCount().subscribe({
      next: (data) => this.pendingCount$.next(data.pending),
      error: () => this.pendingCount$.next(null)
    })
  }

  private buildParams(filters?: ProductLinksFilters | { [key: string]: any }): HttpParams {
    let params = new HttpParams()
    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = (filters as { [key: string]: any })[key]
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value))
        }
      })
    }
    return params
  }

  getLinks(filters?: ProductLinksFilters): Observable<ProductLinksResponse> {
    return this.http.get<ProductLinksResponse>(this.base, {
      params: this.buildParams(filters),
      headers: { 'X-Skip-Loader': 'true' }
    })
  }

  approve(id: number): Observable<{ message: string, seguro: ProductLink }> {
    return this.http.patch<{ message: string, seguro: ProductLink }>(`${this.base}/${id}/approve`, {})
  }

  reject(id: number, reason: string): Observable<{ message: string, seguro: ProductLink }> {
    return this.http.patch<{ message: string, seguro: ProductLink }>(`${this.base}/${id}/reject`, { reason })
  }

  /** Acrescenta cobertura. Abre um pedido novo; o contrato atual segue valendo. */
  addCobertura(id: number, produtoId: number): Observable<{ message: string, seguro: ProductLink }> {
    return this.http.post<{ message: string, seguro: ProductLink }>(
      `${this.base}/${id}/coberturas`, { produto_id: produtoId })
  }

  /** Remove cobertura. Abre um pedido novo; o contrato atual segue valendo. */
  removeCobertura(id: number, produtoId: number): Observable<{ message: string, seguro: ProductLink }> {
    return this.http.delete<{ message: string, seguro: ProductLink }>(
      `${this.base}/${id}/coberturas/${produtoId}`)
  }

  getPendingCount(): Observable<{ pending: number }> {
    return this.http.get<{ pending: number }>(`${this.base}/pending/count`, {
      headers: { 'X-Skip-Loader': 'true' }
    })
  }

  getIndicators(filters?: ProductLinksFilters): Observable<ProductLinksIndicators> {
    return this.http.get<ProductLinksIndicators>(`${this.base}/indicators`, {
      params: this.buildParams(filters),
      headers: { 'X-Skip-Loader': 'true' }
    })
  }

  getVehicles(filters?: { [key: string]: any }): Observable<SeguroVehiclesResponse> {
    return this.http.get<SeguroVehiclesResponse>(`${this.base}/vehicles`, {
      params: this.buildParams(filters),
      headers: { 'X-Skip-Loader': 'true' }
    })
  }

  getVehicleFilterOptions(): Observable<SeguroVehiclesFilterOptions> {
    return this.http.get<SeguroVehiclesFilterOptions>(`${this.base}/vehicles/filter-options`, {
      headers: { 'X-Skip-Loader': 'true' }
    })
  }

  getVehicle(id: number): Observable<SeguroVehicleDetail> {
    return this.http.get<SeguroVehicleDetail>(`${this.base}/vehicles/${id}`)
  }

  /** Todos os dados dos veículos filtrados, em CSV ou Excel (format=xlsx) */
  exportVehicles(filters?: { [key: string]: any }): Observable<HttpResponse<Blob>> {
    return this.downloadExport(`${this.base}/vehicles/export`, filters)
  }

  export(params?: { [key: string]: any }) {
    return this.downloadExport(`${this.base}/export`, params)
  }

  /** Baixa o arquivo; o Excel vem do Lambda como JSON em base64 e vira Blob aqui. */
  private downloadExport(url: string, params?: { [key: string]: any }): Observable<HttpResponse<Blob>> {
    return this.http.get(url, {
      params: this.buildParams(params),
      responseType: 'blob',
      observe: 'response' as const
    }).pipe(
      switchMap((response) => {
        const contentType = response.headers.get('content-type') || ''
        // Se o Lambda retornou JSON com base64 (padrão Traccar)
        if (contentType.includes('application/json') && response.body) {
          return from(response.body.text()).pipe(
            map((text) => {
              try {
                const json = JSON.parse(text as unknown as string)
                if (json?.data && json?.type === 'base64') {
                  const binaryString = atob(json.data)
                  const bytes = new Uint8Array(binaryString.length)
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                  }
                  const blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  })
                  return new HttpResponse<Blob>({
                    body: blob,
                    headers: response.headers,
                    status: response.status,
                    statusText: response.statusText,
                    url: response.url || undefined
                  })
                }
              } catch (_) {
                // queda para retorno original
              }
              return response
            })
          )
        }
        return of(response)
      })
    )
  }
}
